// As peças que as seções das Configurações dividem: o bloco, o rodapé e o campo. (#880)

import { type ReactNode } from 'react';
import type { UltimaAlteracao } from '@cliqcrm/contracts';
import { Botao, Cartao } from '@/shared/ui';

/**
 * Um grupo de campos, com o que ele decide dito em cima.
 *
 * `atualizadoEm` é o que a resposta REALMENTE traz. O desenho pedia o rodapé
 * completo — quem alterou, quando, e com qual justificativa —, e as outras
 * duas partes não existem em `ConfiguracaoDoTenantSchema`: há `atualizado_em`,
 * e mais nada. Inventar um nome seria pior que a data sozinha, porque a tela
 * promete que a justificativa fica na auditoria e alguém acreditaria nela.
 */
export function Bloco({
  titulo,
  apoio,
  ultima,
  aoAlterar,
  children,
}: {
  titulo: string;
  apoio: string;
  ultima?: UltimaAlteracao | null;
  aoAlterar?: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Cartao como="section" variante="folgado" parte="admin__bloco">
      <header className="admin__bloco-cabeca">
        <h2 className="admin__bloco-titulo">{titulo}</h2>
        {aoAlterar && (
          <span className="admin__bloco-acoes">
            <Botao miudo onClick={aoAlterar}>
              Alterar
            </Botao>
          </span>
        )}
      </header>
      <p className="admin__bloco-apoio">{apoio}</p>
      <dl className="admin__campos ficha">{children}</dl>
      {ultima && <Rodape ultima={ultima} />}
    </Cartao>
  );
}

/**
 * Quem alterou, quando, e por quê.
 *
 * ── Por que a justificativa aparece na TELA ───────────────────────────────
 *
 * A seção prometia que "alterar exige justificativa, que fica na auditoria" e
 * não mostrava nenhuma. Promessa sozinha não se verifica — e é o registro à
 * vista que faz a próxima pessoa escrever um motivo de verdade em vez de
 * "ajuste", porque ela sabe que alguém vai ler.
 *
 * `por` vem `null` quando quem alterou saiu da revenda. A frase muda em vez de
 * mostrar um espaço vazio: a alteração aconteceu, e não saber de quem foi é uma
 * informação, não um defeito de carregamento.
 */
function Rodape({ ultima }: { ultima: UltimaAlteracao }): ReactNode {
  const quando = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(
    new Date(ultima.em),
  );

  return (
    <p className="admin__bloco-rodape">
      <span>
        {ultima.por ? `Alterado por ${ultima.por}` : 'Alterado por alguém que não está mais aqui'} ·{' '}
        {quando}
      </span>
      {ultima.justificativa && <q className="admin__bloco-motivo">{ultima.justificativa}</q>}
    </p>
  );
}

export function Campo({
  rotulo,
  valor,
  apoio,
}: {
  rotulo: string;
  valor: string;
  apoio?: string;
}): ReactNode {
  /*
   * Fragmento, e não `<div>`: a `ficha` é uma grade de duas colunas cujos
   * filhos diretos são o `dt` e o `dd`. Envolvidos, os dois viravam UMA célula
   * — que é o que a caixa com borda de antes escondia.
   */
  return (
    <>
      <dt>{rotulo}</dt>
      <dd>
        {valor}
        {apoio && <span className="admin__campo-apoio">{apoio}</span>}
      </dd>
    </>
  );
}
